import fetcher from 'services/graphql/fetcher'

import { getHeaderAkamai } from '../akamai'
import { env } from '../envManager'

export interface ContentStackFetchParams {
	apiKey: string
	accessToken: string
	previewToken?: string
	previewHash?: string
	query: string
	country: string
	variables?: Record<string, string | boolean | number>
	branch?: string
	isServerRequest?: boolean
	isPreview?: boolean
	/** Queryparam to cache POST requests */
	isCacheEnabled?: boolean
}

interface ContentStackFetchError {
	message: string
}

const getUrl = ({
	apiKey,
	isServerRequest,
	isCacheEnabled = false,
	isPreview = false,
}: {
	apiKey: string
	isServerRequest: boolean
	isCacheEnabled?: boolean
	isPreview?: boolean
}): string => {
	const environment =
		env.NEXT_PUBLIC_ENV === 'local' ? 'dev' : env.NEXT_PUBLIC_ENV

	const contentStackUrl = isPreview
		? 'https://eu-graphql-preview.contentstack.com'
		: 'https://eu-graphql.contentstack.com'

	const baseUrl = isServerRequest
		? contentStackUrl
		: `${env.NEXT_PUBLIC_SITE_BASE_URL}/cms-data`

	const path = `/stacks/${apiKey}`

	const urlObject = new URL(`${baseUrl}${path}`)

	urlObject.searchParams.append('environment', environment)

	if (isCacheEnabled && !isPreview) {
		// the only valid value for cache is 'true'
		urlObject.searchParams.append('cache', 'true')
	}

	return urlObject.href
}

const getHeaders = ({
	accessToken,
	previewToken,
	previewHash,
	branch,
}: {
	accessToken: string
	previewToken?: string
	previewHash?: string
	branch?: string
}) => {
	const headers: Record<string, string> = {
		access_token: accessToken,
		...(previewToken && { preview_token: previewToken }),
		...(previewHash && { live_preview: previewHash }),
		...(branch && { branch }),
	}

	return headers
}

export const isContentStackFetchError = (
	error: unknown
): error is ContentStackFetchError[] =>
	Array.isArray(error) && error.length > 0 && error[0].message !== undefined

const contentStackFetch = async <T>({
	apiKey,
	accessToken,
	previewToken,
	previewHash,
	query,
	country,
	branch,
	isServerRequest = true,
	isCacheEnabled = false,
	isPreview = false,
	variables = {},
}: ContentStackFetchParams): Promise<T | null> => {
	let headerAkamai = {}

	if (!isServerRequest) {
		headerAkamai = await getHeaderAkamai()
	}

	const response = await fetcher<T, typeof variables, ContentStackFetchError>({
		url: getUrl({ apiKey, isServerRequest, isCacheEnabled, isPreview }),
		variables: {
			locale: country.toLowerCase(),
			...variables,
		},
		headers: {
			...getHeaders({ accessToken, previewToken, previewHash, branch }),
			...headerAkamai,
		},
		query,
	})
	if (!response) {
		if (env.NEXT_PUBLIC_ENV === 'local') {
			console.error(`Tried to fetch ${query}`)
		}

		throw new Error('Error while fetching data from ContentStack')
	}

	// Response is an array of errors
	if (isContentStackFetchError(response)) {
		throw new Error(response[0].message)
	}

	return response ?? null
}

export default contentStackFetch
